import EventIcon from '@mui/icons-material/Event';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import WorkIcon from '@mui/icons-material/Work';
import {
    Avatar,
    Box, Card, CardContent,
    Chip, CircularProgress,
    Grid,
    Paper,
    Tab, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow,
    Tabs,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

const EmployeeProfile = ({ user, token }) => {
    const [tabValue, setTabValue] = useState(0);
    const [profile, setProfile] = useState(null);
    const [workSessions, setWorkSessions] = useState([]);
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user.employeeId) {
            loadData();
        }
    }, [user.employeeId]);

    const loadData = async () => {
        setLoading(true);
        await Promise.all([
            fetchProfile(),
            fetchWorkSessions(),
            fetchEvents(),
            fetchStats()
        ]);
        setLoading(false);
    };

    const fetchProfile = async () => {
        try {
            const response = await fetch(`${API_URL}/api/employees/${user.employeeId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setProfile(data.employee);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const fetchWorkSessions = async () => {
        try {
            const response = await fetch(
                `${API_URL}/api/work-sessions?employee_id=${user.employeeId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            if (data.success) {
                setWorkSessions(data.data || []);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const fetchEvents = async () => {
        try {
            const response = await fetch(
                `${API_URL}/api/employees/${user.employeeId}/events`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            if (data.success) {
                setEvents(data.events || []);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await fetch(
                `${API_URL}/api/employees/${user.employeeId}/stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            if (data.success) {
                setStats(data.stats || {});
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!profile) {
        return <Typography>Профиль не найден</Typography>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Мой профиль
            </Typography>

            {/* Карточка профиля */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <Avatar sx={{ width: 80, height: 80, bgcolor: '#667eea', fontSize: '2rem' }}>
                                {profile.full_name?.charAt(0)}
                            </Avatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="h5">{profile.full_name}</Typography>
                            <Typography color="textSecondary" gutterBottom>{profile.position}</Typography>
                            <Typography variant="body2">{profile.phone}</Typography>
                            <Typography variant="body2">{profile.email}</Typography>
                        </Grid>
                        <Grid item>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f0f4ff', borderRadius: 2 }}>
                                <Typography variant="h4" color="primary">
                                    {stats.total_shifts || 0}
                                </Typography>
                                <Typography variant="caption">Смен отработано</Typography>
                            </Box>
                        </Grid>
                        <Grid item>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f0fff4', borderRadius: 2 }}>
                                <Typography variant="h4" color="success.main">
                                    ₽{Math.round(stats.total_salary || 0).toLocaleString('ru-RU')}
                                </Typography>
                                <Typography variant="caption">Всего заработано</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Табы */}
            <Card>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab icon={<WorkIcon />} iconPosition="start" label="Мои смены" />
                    <Tab icon={<EventIcon />} iconPosition="start" label="Мои мероприятия" />
                    <Tab icon={<MonetizationOnIcon />} iconPosition="start" label="Зарплата" />
                </Tabs>

                <CardContent>
                    {/* Вкладка "Мои смены" */}
                    {tabValue === 0 && (
                        <Box>
                            {workSessions.length === 0 ? (
                                <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                                    У вас пока нет записей о сменах
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Дата</TableCell>
                                                <TableCell>Тип смены</TableCell>
                                                <TableCell align="right">Часы</TableCell>
                                                <TableCell align="right">Оплата</TableCell>
                                                <TableCell>Статус</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {workSessions.map((session) => (
                                                <TableRow key={session.id}>
                                                    <TableCell>
                                                        {new Date(session.work_date).toLocaleDateString('ru-RU')}
                                                    </TableCell>
                                                    <TableCell>{session.shift_type_name || 'Без типа'}</TableCell>
                                                    <TableCell align="right">{session.hours_worked || 0} ч</TableCell>
                                                    <TableCell align="right">
                                                        ₽{Math.round(session.amount_paid || 0).toLocaleString('ru-RU')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={session.is_paid ? 'Оплачено' : 'Не оплачено'}
                                                            color={session.is_paid ? 'success' : 'warning'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}

                    {/* Вкладка "Мои мероприятия" */}
                    {tabValue === 1 && (
                        <Box>
                            {events.length === 0 ? (
                                <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                                    Вы пока не назначены ни на одно мероприятие
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Дата и время</TableCell>
                                                <TableCell>Название</TableCell>
                                                <TableCell>Место</TableCell>
                                                <TableCell>Статус</TableCell>
                                                <TableCell>Примечания</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {events.map((event) => (
                                                <TableRow key={event.id}>
                                                    <TableCell>
                                                        {new Date(event.event_date).toLocaleString('ru-RU', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </TableCell>
                                                    <TableCell>{event.name}</TableCell>
                                                    <TableCell>{event.location || '-'}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={event.status === 0 ? 'Активно' : 'Завершено'}
                                                            color={event.status === 0 ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{event.notes || '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}

                    {/* Вкладка "Зарплата" */}
                    {tabValue === 2 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>За текущий месяц</Typography>
                                        <Typography variant="h4" color="primary">
                                            ₽{Math.round(stats.current_month_salary || 0).toLocaleString('ru-RU')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>За прошлый месяц</Typography>
                                        <Typography variant="h4" color="success.main">
                                            ₽{Math.round(stats.last_month_salary || 0).toLocaleString('ru-RU')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>Всего заработано</Typography>
                                        <Typography variant="h4" color="info.main">
                                            ₽{Math.round(stats.total_salary || 0).toLocaleString('ru-RU')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>Всего часов отработано</Typography>
                                        <Typography variant="h4">
                                            {Math.round(stats.total_hours || 0)} часов
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>Оплачено</Typography>
                                        <Typography variant="h4" color="success.main">
                                            ₽{Math.round(stats.paid_salary || 0).toLocaleString('ru-RU')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default EmployeeProfile;
